import React, { useState } from 'react';
import { T, font, mono, trackUrl } from '../utils/theme';
import * as api from '../utils/api';
import { Card, SBadge, SearchBar, Btn, IcMsg, matchesSearch } from './UI';
import { money } from '../utils/money';

export default function SupplierPastOrders({ lang, supplier, products, allProducts, shipments, shipmentComments, reload, Lk }) {
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [openComment, setOpenComment] = useState(null);
  const [commentText, setCommentText] = useState('');

  const received = shipments.filter(s => s.supplier_id === supplier.id && s.status === 'received');
  const prodLookup = allProducts || products;

  const filtered = received.filter(s => {
    if (!search) return true;
    const prods = (s.items || []).map(i => prodLookup.find(p => p.id === i.product_id)).filter(Boolean);
    return matchesSearch(search, s.tracking || '') || prods.some(p => matchesSearch(search, p.name) || matchesSearch(search, p.sku || ''));
  });

  const getProductName = (pid) => {
    const p = prodLookup.find(x => x.id === pid);
    if (!p) return pid;
    return p.deleted ? `${p.name} (Deleted)` : p.name;
  };

  const doSendComment = async (shipId) => {
    if (!commentText.trim()) return;
    await api.addShipmentComment({ shipment_id: shipId, author: supplier.name, author_type: 'supplier', text: commentText.trim() });
    setCommentText('');
    await reload();
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 22px', fontSize: 22, fontWeight: 900, color: T.text }}>{Lk('pastOrders')} ({received.length})</h2>
      <SearchBar sticky value={search} onChange={setSearch} placeholder={`${Lk('searchProduct')} / ${Lk('searchTracking')} / ${Lk('searchSku')}`} style={{ marginBottom: 16 }} />

      {filtered.length === 0 ? (
        <div style={{ color: T.dim, fontSize: 13, fontStyle: 'italic', padding: 20 }}>{search ? Lk('noResults') : Lk('noData')}</div>
      ) : filtered.map(s => {
        const isOpen = expanded[s.id];
        const tUrl = trackUrl(s.courier, s.tracking);
        const hasMismatch = (s.items || []).some(i => i.received && i.received_qty !== i.ship_qty);
        const comments = (shipmentComments || []).filter(c => c.shipment_id === s.id);
        const hasComments = comments.length > 0;
        const isCommentOpen = openComment === s.id;

        return (
          <Card key={s.id} style={{ marginBottom: 10, cursor: 'pointer', borderColor: hasMismatch ? T.orange + '80' : T.border, background: hasMismatch ? T.orangeBg : T.surface }}
            onClick={() => setExpanded({ ...expanded, [s.id]: !isOpen })}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 14, color: T.dim }}>{isOpen ? '▼' : '▶'}</span>
                <span style={{ fontSize: 11, color: T.dim, fontFamily: mono }}>{s.received_date || s.created_at}</span>
                {tUrl && s.tracking !== '—' ? (
                  <a href={tUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontFamily: mono, fontSize: 12, color: T.accent, textDecoration: 'underline' }}>{s.tracking}</a>
                ) : (
                  <span style={{ fontFamily: mono, fontSize: 12, color: T.muted }}>{s.tracking}</span>
                )}
                {s.courier && s.courier !== '—' && <SBadge label={s.courier} color={T.muted} bg={T.surfaceAlt} />}
                <span style={{ fontSize: 11, color: T.dim }}>{(s.items || []).length} items</span>
                {hasComments && <span style={{ width: 10, height: 10, borderRadius: 5, background: T.warn, display: 'inline-block' }} title="Has comments" />}
                {hasMismatch && <SBadge label={Lk('mismatch')} color={T.orange} bg={T.orangeBg} />}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: T.dim }}>{lang === 'zh' ? '已交付' : 'Delivered'} {s.received_date}</span>
                <SBadge label={lang === 'zh' ? '已交付' : 'Delivered'} color={T.success} bg={T.successBg} />
                <span onClick={e => { e.stopPropagation(); setOpenComment(isCommentOpen ? null : s.id); setCommentText(''); }} style={{ cursor: 'pointer' }}>{IcMsg}</span>
              </div>
            </div>

            {isOpen && (
              <div style={{ marginTop: 12 }} onClick={e => e.stopPropagation()}>
                {(() => {
                  let subtotal = 0;
                  const rows = (s.items || []).map(item => {
                    const p = prodLookup.find(x => x.id === item.product_id);
                    const mismatch = item.received_qty != null && item.received_qty !== item.ship_qty;
                    const price = item.unit_price || p?.quotes?.[supplier.id]?.price || p?.cost_price || 0;
                    const qty = item.ship_qty || 0;
                    const lineSub = price * qty;
                    subtotal += lineSub;
                    return (
                      <tr key={item.product_id} style={{ background: mismatch ? T.orangeBg : 'transparent' }}>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}`, color: T.text, fontWeight: 600 }}>{getProductName(item.product_id)}</td>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.muted, fontSize: 11 }}>{p?.sku || '—'}</td>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: mono }}>{qty}</td>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.muted }}>{money(price)}</td>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.text }}>{money(lineSub)}</td>
                      </tr>
                    );
                  });
                  const total = subtotal + (s.shipping_cost || 0);
                  return (
                    <>
                      <table style={{ width: '100%', fontSize: 12, fontFamily: font, borderCollapse: 'collapse' }}>
                        <thead><tr>
                          {[Lk('productName'), Lk('sku'), lang === 'zh' ? '发货' : 'Shipped', lang === 'zh' ? '单价' : 'Unit Price', 'Subtotal'].map((h, i) => (
                            <th key={i} style={{ textAlign: 'left', padding: '6px 10px', background: T.surfaceAlt, color: T.dim, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', borderBottom: `1px solid ${T.border}` }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>{rows}</tbody>
                      </table>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 6, padding: '6px 0', borderTop: `1px solid ${T.border}30` }}>
                        <span style={{ fontSize: 11, color: T.dim }}>Subtotal: <span style={{ fontFamily: mono, color: T.muted }}>{money(subtotal)}</span></span>
                        <span style={{ fontSize: 11, color: T.dim }}>{lang === 'zh' ? '运费' : 'Shipping'}: <span style={{ fontFamily: mono, color: T.muted }}>{money((s.shipping_cost || 0))}</span></span>
                        <span style={{ fontSize: 12, fontWeight: 800, color: T.accent }}>Total: <span style={{ fontFamily: mono }}>{money(total)}</span></span>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Comments */}
            {isCommentOpen && (
              <div style={{ marginTop: 10, padding: 12, background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
                <div style={{ fontSize: 10, fontWeight: 800, color: T.dim, textTransform: 'uppercase', marginBottom: 8 }}>{lang === 'zh' ? '物流备注' : 'Shipment Notes'}</div>
                {comments.map(c => (
                  <div key={c.id} style={{ fontSize: 12, padding: '4px 0', borderBottom: `1px solid ${T.border}20` }}>
                    <span style={{ fontWeight: 700, color: c.author_type === 'admin' ? T.accent : T.success }}>{c.author}: </span>
                    <span style={{ color: T.text }}>{c.text}</span>
                    <span style={{ color: T.dim, fontSize: 10, marginLeft: 6 }}>{c.created_at?.split('T')[0]}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder={lang === 'zh' ? '添加备注...' : 'Add note...'}
                    onKeyDown={e => { if (e.key === 'Enter') doSendComment(s.id); }}
                    style={{ flex: 1, fontFamily: font, fontSize: 12, padding: '7px 10px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none' }} />
                  <Btn variant="small" style={{ padding: '5px 10px', fontSize: 11 }} onClick={() => doSendComment(s.id)}>{lang === 'zh' ? '发送' : 'Send'}</Btn>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
